import { useCallback, useEffect, useRef, useState } from 'react';
import { getWebSocketHttpBase } from '../lib/apiBase';
import { api } from '../lib/api';

const MAX_EVENTS = 200;
const PING_MS = 30000;
const EVENT_FLUSH_MS = 80;

function toWsBase(httpBase) {
  const b = String(httpBase || '').replace(/\/$/, '');
  if (!b) return '';
  return b.replace(/^https:\/\//i, 'wss://').replace(/^http:\/\//i, 'ws://');
}

/**
 * Browser WebSocket to the embedded AERTAX LangGraph agent (`/api/dojo-lab/aertago-agent/ws`).
 * Protocol matches **aertax-master** webapp / vendored `websocket_api.py`: `{ type, payload }`
 * with `init` → `connected`, then `query`, `approval`, `tool_confirmation`, `answer`, `guidance`,
 * `skill_inject`, `stop`, `resume`, `ping`.
 */
export function useAertagoEmbeddedAgentWs() {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastConnectArgsRef = useRef(null);
  const [wsState, setWsState] = useState('idle');
  const [agentInited, setAgentInited] = useState(false);
  const [events, setEvents] = useState([]);
  const [lastEventAt, setLastEventAt] = useState(0);
  const [lastLatencyMs, setLastLatencyMs] = useState(null);
  const pendingLatencyStartRef = useRef(0);
  const [autoReconnectEnabled, setAutoReconnectEnabled] = useState(true);
  const [approvalRequest, setApprovalRequest] = useState(null);
  const [questionRequest, setQuestionRequest] = useState(null);
  const [toolConfirmationRequest, setToolConfirmationRequest] = useState(null);
  const [phaseUpdate, setPhaseUpdate] = useState(null);
  const [todoList, setTodoList] = useState([]);
  const [streamingThought, setStreamingThought] = useState('');

  // Batch high-frequency WS events to keep the console snappy.
  const pendingEventsRef = useRef([]);
  const flushTimerRef = useRef(null);

  const flushEvents = useCallback(() => {
    flushTimerRef.current = null;
    const batch = pendingEventsRef.current;
    if (!batch.length) return;
    pendingEventsRef.current = [];
    setLastEventAt(Date.now());
    setEvents((prev) => {
      const next = [...prev, ...batch];
      return next.length > MAX_EVENTS ? next.slice(-MAX_EVENTS) : next;
    });
  }, []);

  const pushEvent = useCallback(
    (type, payload) => {
      const now = Date.now();
      const id = `${now}-${Math.random().toString(36).slice(2, 9)}`;
      pendingEventsRef.current.push({ id, at: now, type, payload });
      if (pendingEventsRef.current.length > MAX_EVENTS) {
        pendingEventsRef.current = pendingEventsRef.current.slice(-MAX_EVENTS);
      }
      if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flushEvents, EVENT_FLUSH_MS);
      }
    },
    [flushEvents],
  );

  const stopPing = useCallback(() => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    stopPing();
    try {
      wsRef.current?.close();
    } catch {
      /* ignore */
    }
    wsRef.current = null;
    setWsState('closed');
    setAgentInited(false);
    setApprovalRequest(null);
    setQuestionRequest(null);
    setToolConfirmationRequest(null);
    setStreamingThought('');
    reconnectAttemptsRef.current = 0;
  }, [stopPing]);

  useEffect(() => () => disconnect(), [disconnect]);

  const sendRaw = useCallback(
    (type, payload) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        pushEvent('client_error', { message: 'Socket not open', attempted: type });
        return false;
      }
      wsRef.current.send(JSON.stringify({ type, payload: payload || {} }));
      return true;
    },
    [pushEvent],
  );

  const connect = useCallback(
    ({ userId, projectId, sessionId, graphViewCypher } = {}) => {
      lastConnectArgsRef.current = { userId, projectId, sessionId, graphViewCypher };
      disconnect();
      const token =
        api.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('aerta_token') : null);
      if (!token) {
        pushEvent('client_error', { message: 'No API token — log in first.' });
        return;
      }
      let httpBase = getWebSocketHttpBase();
      if (!httpBase && typeof window !== 'undefined') {
        httpBase = `${window.location.protocol}//${window.location.host}`;
      }
      const wsBase = toWsBase(httpBase);
      if (!wsBase) {
        pushEvent('client_error', { message: 'Could not resolve WebSocket base URL.' });
        return;
      }
      const url = `${wsBase}/api/dojo-lab/aertago-agent/ws?token=${encodeURIComponent(token)}`;

      setWsState('connecting');
      pushEvent('client', { message: 'connecting…' });

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsState('open');
        setAgentInited(false);
        reconnectAttemptsRef.current = 0;
        pushEvent('client', { message: 'socket open — init' });
        const initPayload = {
          user_id: String(userId || 'operator'),
          project_id: String(projectId || 'default'),
          session_id: String(sessionId || 'session-1'),
        };
        if (graphViewCypher && String(graphViewCypher).trim()) {
          initPayload.graph_view_cypher = String(graphViewCypher).trim();
        }
        ws.send(JSON.stringify({ type: 'init', payload: initPayload }));
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          const typ = data?.type || '?';
          const payload = data?.payload;

          pushEvent(typ, payload);
          setLastEventAt(Date.now());
          if (pendingLatencyStartRef.current && typ && typ !== 'client' && typ !== 'ping') {
            setLastLatencyMs(Math.max(0, Date.now() - pendingLatencyStartRef.current));
            pendingLatencyStartRef.current = 0;
          }

          if (typ === 'connected') {
            setAgentInited(true);
            stopPing();
            pingRef.current = setInterval(() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping', payload: {} }));
              }
            }, PING_MS);
          }

          if (typ === 'approval_request') {
            setApprovalRequest(payload && typeof payload === 'object' ? payload : {});
          }
          if (typ === 'question_request') {
            setQuestionRequest(payload && typeof payload === 'object' ? payload : {});
          }
          if (typ === 'tool_confirmation_request') {
            setToolConfirmationRequest(payload && typeof payload === 'object' ? payload : {});
          }
          if (typ === 'phase_update' && payload && typeof payload === 'object') {
            setPhaseUpdate(payload);
          }
          if (typ === 'todo_update' && payload && typeof payload === 'object' && Array.isArray(payload.todo_list)) {
            setTodoList(payload.todo_list);
          }
          if (typ === 'thinking' && payload && typeof payload === 'object') {
            const t = [payload.thought, payload.reasoning].filter(Boolean).join('\n\n');
            setStreamingThought(t || '');
          }
          if (typ === 'thinking_chunk' && payload?.chunk != null) {
            setStreamingThought((prev) => prev + String(payload.chunk));
          }
          if (typ === 'response' && payload?.answer != null) {
            setStreamingThought('');
          }
        } catch {
          pushEvent('raw', { text: String(ev.data).slice(0, 500) });
        }
      };

      ws.onerror = () => {
        pushEvent('client_error', { message: 'WebSocket error (see network tab)' });
      };

      ws.onclose = (evt) => {
        stopPing();
        flushEvents();
        pushEvent('client', { message: `closed code=${evt.code} ${evt.reason || ''}` });
        setWsState('closed');
        setAgentInited(false);
        if (wsRef.current === ws) wsRef.current = null;

        if (autoReconnectEnabled && lastConnectArgsRef.current && evt?.code !== 4401 && evt?.code !== 4403) {
          const attempt = reconnectAttemptsRef.current + 1;
          reconnectAttemptsRef.current = attempt;
          const delay = Math.min(10000, 800 * Math.pow(1.6, attempt - 1));
          pushEvent('client', { message: `reconnecting in ${Math.round(delay)}ms (attempt ${attempt})` });
          reconnectRef.current = setTimeout(() => {
            reconnectRef.current = null;
            connect(lastConnectArgsRef.current);
          }, delay);
        }
      };
    },
    [autoReconnectEnabled, disconnect, flushEvents, pushEvent, stopPing],
  );

  const sendPing = useCallback(() => {
    sendRaw('ping', {});
  }, [sendRaw]);

  const sendQuery = useCallback(
    (question) => {
      const q = String(question || '').trim();
      if (!q) return;
      if (sendRaw('query', { question: q })) {
        setStreamingThought('');
        setApprovalRequest(null);
        setQuestionRequest(null);
        setToolConfirmationRequest(null);
        setLastLatencyMs(null);
        pendingLatencyStartRef.current = Date.now();
        pushEvent('client', { message: 'query sent' });
      }
    },
    [pushEvent, sendRaw],
  );

  const sendApproval = useCallback(
    (decision, modification) => {
      if (!sendRaw('approval', { decision, modification: modification || undefined })) return;
      setApprovalRequest(null);
    },
    [sendRaw],
  );

  const sendToolConfirmation = useCallback(
    (decision, modifications) => {
      const p = { decision };
      if (modifications && typeof modifications === 'object') p.modifications = modifications;
      if (!sendRaw('tool_confirmation', p)) return;
      setToolConfirmationRequest(null);
    },
    [sendRaw],
  );

  const sendAnswer = useCallback(
    (answer) => {
      const a = String(answer || '').trim();
      if (!a) return;
      if (!sendRaw('answer', { answer: a })) return;
      setQuestionRequest(null);
    },
    [sendRaw],
  );

  const sendGuidance = useCallback(
    (message) => {
      const m = String(message || '').trim();
      if (!m) return;
      sendRaw('guidance', { message: m });
    },
    [sendRaw],
  );

  const sendSkillInject = useCallback(
    (payload) => {
      if (!payload?.skill_id || !payload?.content) return;
      sendRaw('skill_inject', {
        skill_id: String(payload.skill_id),
        skill_name: String(payload.skill_name || payload.skill_id),
        content: String(payload.content),
      });
    },
    [sendRaw],
  );

  const sendStop = useCallback(() => {
    sendRaw('stop', {});
  }, [sendRaw]);

  const sendResume = useCallback(() => {
    sendRaw('resume', {});
  }, [sendRaw]);

  const clearEvents = useCallback(() => {
    setEvents([]);
    pendingEventsRef.current = [];
    setLastEventAt(0);
    setLastLatencyMs(null);
    pendingLatencyStartRef.current = 0;
  }, []);

  return {
    connect,
    disconnect,
    setAutoReconnectEnabled,
    autoReconnectEnabled,
    sendPing,
    sendQuery,
    sendApproval,
    sendToolConfirmation,
    sendAnswer,
    sendGuidance,
    sendSkillInject,
    sendStop,
    sendResume,
    clearEvents,
    wsState,
    agentInited,
    events,
    lastEventAt,
    lastLatencyMs,
    approvalRequest,
    questionRequest,
    toolConfirmationRequest,
    phaseUpdate,
    todoList,
    streamingThought,
  };
}
